/**
 * Track recently-visited posts in localStorage so the command palette
 * can surface them when opened with no query — common UX shortcut for
 * "the post I was just on".
 *
 * Stored as a JSON array of `{id, title, slug, visitedAt}` with the
 * most recent first. Capped at 10 entries (palette shows 5; extras
 * keep history for tab-back cases). Visits to the same id move the
 * entry to the top and refresh the timestamp instead of duplicating.
 *
 * localStorage is per-browser-per-origin, so each device the admin
 * uses builds its own list — that's the right model for a "where
 * have I been on THIS machine?" surface.
 */

const STORAGE_KEY = 'news1cms:recent-posts';
const MAX_ENTRIES = 10;

export interface RecentPost {
  id: string;
  title: string;
  slug: string;
  visitedAt: number;
}

export function loadRecentPosts(): RecentPost[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (p): p is RecentPost =>
        typeof p === 'object' &&
        p !== null &&
        typeof (p as RecentPost).id === 'string' &&
        typeof (p as RecentPost).title === 'string' &&
        typeof (p as RecentPost).slug === 'string' &&
        typeof (p as RecentPost).visitedAt === 'number',
    );
  } catch {
    return [];
  }
}

export function trackPostVisit(entry: { id: string; title: string; slug: string }): void {
  if (typeof window === 'undefined') return;
  try {
    const current = loadRecentPosts();
    const withoutThis = current.filter((p) => p.id !== entry.id);
    const next = [{ ...entry, visitedAt: Date.now() }, ...withoutThis].slice(0, MAX_ENTRIES);
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // Storage full / disabled / quota — silent fail; this is a
    // nice-to-have, not load-bearing.
  }
}

/** Remove an id from the recents list. Useful when a post is
 *  deleted — the palette shouldn't keep surfacing a dead link. */
export function forgetPost(id: string): void {
  if (typeof window === 'undefined') return;
  try {
    const next = loadRecentPosts().filter((p) => p.id !== id);
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // ignore
  }
}
